import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Autocomplete,
  InputAdornment,
  TextField,
  useTheme,
} from '@mui/material';

import ClearIcon from '@mui/icons-material/Clear';
import LogoutIcon from '@mui/icons-material/Logout';

import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { ReactComponent as LightTheme } from '../../assets/LightTheme.svg';
import { ReactComponent as DarkTheme } from '../../assets/DarkTheme.svg';
import { ReactComponent as DropdownArrow } from '../../assets/DropdownArrow.svg';
import { ReactComponent as SuperAdminLogo } from '../../assets/SuperAdminLogo.svg';

import useOrganization from '../../hooks/UseOrganization';
import { commonSlice } from '../../state/reducers/common';
import useAuth from '../../hooks/useAuth';
import SwalToast from '../Common/SwalToast';
import CreateEditOrgModal from '../Organization/CreateEditOrgModal';

const AppHeader = ({ userInfo, organizationList }) => {
  const filteredOrgList = useMemo(() => {
    return organizationList?.filter((option) => option.is_active) || [];
  }, [organizationList]);

  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const anchorRef = useRef();
  const inputRef = useRef();

  const userTheme = useSelector((state) => state.common.userTheme);
  const subadmin = useSelector((state) => state.common.subAdminBoolean);
  const orgId = useSelector((state) => state.common.orgId);
  const organization = useSelector((state) => state.common.orgName);
  const { changeTheme, getPermission, organizationDetailsList } =
    useOrganization();
  const { logoutRequest } = useAuth();

  const [anchorEl, setAnchorEl] = useState(null);
  const [inputValueOrganization, setInputValueOrganization] = useState('');
  const [selectOrganizationOpen, setSelectOrganizationOpen] = useState(false);
  const [value, setValue] = useState([]);
  const [open, setOpen] = useState(false);

  const imgStyle = {
    width: '32px',
    height: '32px',
    marginTop: '6px',
    objectFit: 'contain',
  };

  /** Utility Functions */

  const getFirstLetter = useCallback((name) => {
    return name ? name.charAt(0).toUpperCase() : '';
  }, []);

  const getOptionLabel = useCallback(
    (_organization) => _organization?.name || '',
    []
  );

  const isOptionEqualToValue = useCallback(
    (option, _value) => option.id === _value.id,
    []
  );

  /** Handlers */

  const handleClearInputValue = useCallback(() => {
    setInputValueOrganization('');
  }, []);

  const handleOrganizationOpen = useCallback(
    () => setSelectOrganizationOpen(true),
    []
  );
  const handleOrganizationClose = useCallback(
    () => setSelectOrganizationOpen(false),
    []
  );
  const handleOpen = useCallback(
    (event) => setAnchorEl(event.currentTarget),
    []
  );
  const handleClose = useCallback(() => setAnchorEl(null), []);

  useEffect(() => {
    if (orgId && organizationList?.length) {
      const matched = organizationList.find((org) => org.id === orgId);
      if (matched) setValue(matched);
    }
  }, [organizationList]);

  const handleThemeChange = useCallback(() => {
    const newTheme = userTheme === 'light' ? 'dark' : 'light';
    changeTheme({ theme: newTheme }).then(() => {
      getPermission();
      handleClose();
    });
  }, [userTheme, changeTheme, getPermission, handleClose]);

  const handleLogOut = useCallback(async () => {
    await logoutRequest({});
    navigate('/login');
  }, [logoutRequest, navigate]);

  const handleAutoCompleteInputChange = useCallback((_, newValue) => {
    setInputValueOrganization(newValue);
  }, []);

  const handleAutoCompleteChange = useCallback(
    (_, newValue) => {
      setValue(newValue);
      // eslint-disable-next-line react/prop-types
      organizationList?.forEach((org) => {
        if (newValue && org?.id === Number(newValue?.id)) {
          dispatch(commonSlice.actions.setOrgName(org?.name));
          dispatch(commonSlice.actions.setOrg(org?.id));
          SwalToast({
            icon: 'success',
            title: `Organization ${org?.name} activated successfully.`,
          });
        }
      });
    },
    [organizationList, setValue]
  );

  /** Styling Helpers */

  const appBarSx = useCallback(
    (themes) => ({ zIndex: themes.zIndex.drawer + 1 }),
    []
  );

  const autoCompleteSx = useCallback(
    () => ({
      width: 200,
      height: '100%',
      borderRadius: '5px',
      '& .MuiAutocomplete-endAdornment': {
        right: '10px !important',
        position: 'absolute',
      },
      '& .MuiAutocomplete-popupIndicator': {
        ariaHidden: 'true',
      },
      '& .MuiAutocomplete-clearIndicator': {
        display: 'none !important',
      },
    }),
    []
  );

  /** Autocomplete Input */

  const handleAutoCompleteRender = useCallback(
    (params) => (
      <TextField
        {...params}
        title={organization || 'Search organization'}
        placeholder={organization || 'Search organization'}
        variant="outlined"
        inputRef={inputRef}
        aria-haspopup="listbox"
        aria-expanded={!!inputValueOrganization}
        sx={() => ({
          marginTop: 0,
        })}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {inputValueOrganization && (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    aria-label="clear"
                    onClick={handleClearInputValue}
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    ),
    [inputRef, inputValueOrganization, handleClearInputValue]
  );

  const renderOption = useCallback(
    (props, option) => (
      <Box
        component="li"
        {...props}
        key={option?.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <Typography
          variant="body2"
          title={option?.name}
          sx={{
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {option?.name}
        </Typography>
      </Box>
    ),
    []
  );

  const handleOpenCreateOrgModal = useCallback(() => {
    setOpen(true);
  }, []);

  /** 🧱 JSX */

  return (
    <AppBar position="absolute" sx={appBarSx} elevation={2}>
      <Toolbar>
        {/* App Title */}
        <Box
          flexGrow={1}
          aria-label="App Header Logo"
          flexDirection="row"
          display="flex"
          alignItems="center"
        >
          <Box mr={0.7}>
            <SuperAdminLogo
              style={{ ...imgStyle, color: theme.palette.primary.main }}
            />
          </Box>
          <Box>
            <Typography variant="logo"> ADMIN PANEL</Typography>
          </Box>
        </Box>

        {/* Organization Section */}
        <Box display="flex" alignItems="center" gap={1} width={400}>
          <Button
            variant="contained"
            title={
              subadmin
                ? 'You are not authorized to access this!'
                : 'Create Organization'
            }
            disableRipple
            size="large"
            disabled={subadmin}
            onClick={handleOpenCreateOrgModal}
          >
            + Create Organization
          </Button>

          <Autocomplete
            disabled={subadmin}
            open={selectOrganizationOpen}
            onOpen={handleOrganizationOpen}
            onClose={handleOrganizationClose}
            sx={autoCompleteSx}
            value={value}
            disableClearable
            id="app_header_nav_bar_select_organization"
            inputValue={inputValueOrganization}
            options={filteredOrgList}
            noOptionsText="No organization available."
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            onInputChange={handleAutoCompleteInputChange}
            onChange={handleAutoCompleteChange}
            renderInput={handleAutoCompleteRender}
            popupIcon={<DropdownArrow />}
            renderOption={renderOption}
            size="small"
          />
        </Box>

        {/* User Avatar */}
        <Box>
          <IconButton
            aria-label={userInfo?.full_name}
            onClick={handleOpen}
            ref={anchorRef}
            color="primary"
            title={userInfo?.full_name}
          >
            <Avatar
              sx={{
                width: 28,
                height: 28,
                backgroundColor: theme.palette.primary.main,
              }}
            >
              {getFirstLetter(userInfo?.full_name)}
            </Avatar>
          </IconButton>
        </Box>

        {/* User Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          sx={{ top: '8px' }}
        >
          <MenuItem disabled>{userInfo?.full_name}</MenuItem>

          <MenuItem onClick={handleThemeChange}>
            <ListItemIcon>
              {userTheme === 'light' ? (
                <LightTheme color={theme.palette.primary.main} />
              ) : (
                <DarkTheme color={theme.palette.primary.main} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                userTheme === 'light'
                  ? 'Switch to dark mode'
                  : 'Switch to light mode'
              }
              primaryTypographyProps={{
                sx: { color: theme.palette.primary.main, fontWeight: 600 },
              }}
            />
          </MenuItem>

          <MenuItem onClick={handleLogOut}>
            <ListItemIcon>
              <LogoutIcon sx={{ color: theme.palette.primary.main }} />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                sx: { color: theme.palette.primary.main, fontWeight: 600 },
              }}
            />
          </MenuItem>
        </Menu>
      </Toolbar>
      {open && (
        <CreateEditOrgModal
          statOfForm="create"
          open={open}
          setOpen={setOpen}
          organizationDetailsList={organizationDetailsList}
        />
      )}
    </AppBar>
  );
};

AppHeader.propTypes = {
  userInfo: PropTypes.shape({
    full_name: PropTypes.string,
    email: PropTypes.string,
  }).isRequired,
  organizationList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ).isRequired,
};

export default AppHeader;
